import { useState } from "react";
import { useNavigate } from "react-router";
import { TextField } from "../common/inputFiled";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import oneSignalService from "../../services/oneSignalService";
import { persistUserSession } from "../../utils/authSession";

export default function Login () {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalMessage, setPlanModalMessage] = useState("");

  const getDeviceName = () => {
    const userAgent = navigator.userAgent || "";
    const platform = navigator.userAgentData?.platform || navigator.platform || "Unknown device";

    if (/Android/i.test(userAgent)) return "Android";
    if (/iPhone|iPad|iPod/i.test(userAgent)) return "iPhone/iPad";
    if (/Windows/i.test(userAgent)) return "Windows PC";
    if (/Macintosh|Mac OS X/i.test(userAgent)) return "Mac";
    if (/Linux/i.test(userAgent)) return "Linux";

    return platform;
  };

  const getClientIpAddress = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (!response.ok) return "";

      const data = await response.json();
      return data?.ip || "";
    } catch (error) {
      return "";
    }
  };

  // 🧩 Handle Input Change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // 🧠 Handle Login Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      // ✅ Validate API Base URL
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

      // Debug: Log environment variable status
    
      if (!apiBaseUrl || apiBaseUrl === 'undefined') {
        const errorMsg = "API configuration error: REACT_APP_API_BASE_URL is not set. The application needs to be rebuilt with the correct environment variable.";
        toast.error("Server configuration error. Please contact support.");
        throw new Error(errorMsg);
      }

      const apiUrl = `${apiBaseUrl}/login`;
      const [deviceName, ipAddress] = await Promise.all([
        Promise.resolve(getDeviceName()),
        getClientIpAddress(),
      ]);
   
      const payload = new URLSearchParams({
        email: formData.email,
        password: formData.password,
        deviceType: "Website",
        device_name: deviceName,
        ip_address: ipAddress,
      });

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });


      // ✅ Handle non-JSON responses
      let data;
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error(`Server returned ${res.status}: ${res.statusText}. Response: ${text.substring(0, 100)}`);
      }

      if (!res.ok) {
        throw new Error(data.message || `Login failed with status ${res.status}`);
      }

      const user = data?.data?.[0]?.user;
      const { status, message } = data;

      // Handle plan-restriction response: show modal and stop login flow
      if (status === "0") {
        const msg = message || "Your account cannot sign in via website. Please upgrade or use the mobile app.";
        setPlanModalMessage(msg);
        setPlanModalOpen(true);
        return;
      }


      // ✅ Store token securely in cookies
      // Check multiple possible token locations
      const token = user?.token || data?.token || data?.data?.[0]?.token;

      if (token) {
        // Note: secure: false for HTTP, true for HTTPS
        // Check if we're on HTTPS
        const isSecure = window.location.protocol === 'https:';

        Cookies.set("auth_token", token, {
          expires: formData.rememberMe ? 7 : 1, // 7 days if rememberMe is checked, else 1 day
          secure: isSecure, // Only use secure cookies on HTTPS
          sameSite: "strict",
          path: "/", // Make cookie available for all paths
        });


      } else {
        throw new Error("No authentication token received from server");
      }

      toast.success("Login successful!");
      const userData = user || data?.data?.[0]?.user || {};

      persistUserSession(userData, { verificationType: status });

      // New flow: if invite code was saved pre-login, apply it immediately after login.
      const pendingInviteCode = (localStorage.getItem("pending_invitation_code") || "").trim();
      const emailForInvite = userData.email || formData.email;
      if (pendingInviteCode && emailForInvite && apiBaseUrl) {
        try {
          const invitePayload = new URLSearchParams({
            invite: pendingInviteCode,
            email: emailForInvite,
          });

          const inviteResponse = await fetch(`${apiBaseUrl}/invite/apply`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: invitePayload.toString(),
          });

          const inviteData = await inviteResponse.json();

          if (inviteResponse.ok) {
            localStorage.removeItem("pending_invitation_code");
            toast.success("Invitation code validated successfully!");
            navigate("/contact", { replace: true });
            return;
          }

          toast.error(inviteData.message || "Invitation code validation failed.");
        } catch (inviteErr) {
          }
      }

      // Associate user with OneSignal for targeted push notifications
      if (userData.user_id) {
        oneSignalService.setExternalUserId(userData.user_id)
          .catch(() => {});
      }


      // ✅ Small delay to ensure cookie is set before navigation
      // Redirect logic using React Router (no page reload)
      setTimeout(() => {
        const choosePlanRequired =
          userData.choose_plan_required === 1 ||
          userData.choose_plan_required === "1";
        const paymentPending =
          userData.payment_status === 0 ||
          userData.payment_status === "0";
        const emailVerified =
          userData.email_verified === 1 ||
          userData.email_verified === "1";

        if (status === "4") {
          navigate("/wait-list", { replace: true });
        } else if (status === "2" && !emailVerified) {
          navigate("/otp", { replace: true });
        } else if (status === "3") {
          navigate("/otp", { replace: true });
        } else if (status === "5") {
          navigate("/payment", { replace: true });
        } else if (status === "1" && (paymentPending || choosePlanRequired)) {
          navigate("/payment", { replace: true });
        } else if (status === "1" && emailVerified) {
          navigate("/contact", { replace: true });
        } else {
          throw new Error(message || "Unexpected login status");
        }
      }, 100);
    } catch (err) {
      // ✅ More user-friendly error messages
      let errorMessage = err.message;
      if (err.message.includes("404") || err.message.includes("Not Found")) {
        errorMessage = "API endpoint not found. Please check server configuration.";
      } else if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err.message.includes("REACT_APP_API_BASE_URL")) {
        errorMessage = "Server configuration error. Please contact support.";
      }
      toast.dismiss();

      toast.error(errorMessage);
      setErrors({ api: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-6 sm:py-8">
      <div className="relative w-full max-w-[520px] p-4 sm:p-8 md:p-10">
        {/* Logo */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
            <img src={`${process.env.PUBLIC_URL || ""}/logo.svg`} alt=" PhoneBookPlus Logo" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
             PhoneBookPlus
          </h1>
        </div>

        {/* Login Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField
            id="email"
            type="email"
            name="email"
            label="Email"
            placeholder="Enter Email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          <TextField
            id="password"
            type="password"
            name="password"
            label="Password"
            placeholder="Enter Password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
          />

          {errors.api && (
            <p className="text-red-500 text-sm font-medium">{errors.api}</p>
          )}

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                name="rememberMe"
                className="h-4 w-4 accent-[#7C4DFF] rounded border-gray-300"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              <span className="text-sm text-gray-700">Remember Me</span>
            </label>
            <a href="/forgot-password" className="flex justify-end text-[#7c4ff9] text-sm">Forgot password?</a>
          </div>


          <button
            type="submit"
            disabled={loading}
            className={`w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 transition
              ${loading
                ? "opacity-70 cursor-not-allowed"
                : "hover:brightness-105 active:scale-[0.99]"
              }`}
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
          <p className="text-center text-sm text-gray-600">
            Don't have an account?{" "}
            <a href="/sign-up" className="text-[#7C4DFF] underline">
              Sign up here
            </a>
          </p>
        </form>
        {/* Plan restriction modal */}
        {planModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md bg-white rounded-3xl p-6 mx-4">
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                    <img src={`../no-data/no-personal.svg`} alt="Warning" className="h-10 w-10" />
                  </div>
                </div>
                <h2 className="text-lg font-semibold mb-2">Account restricted</h2>
                <p className="text-sm text-gray-700 mb-4">{planModalMessage}</p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  className="submit-button"
                  onClick={() => setPlanModalOpen(false)}
                >
                  OK
                </button>
            
              </div>
            </div>
          </div>
        )}

        <div className="absolute -right-[334px] -bottom-10 hidden items-center gap-3 text-md min-[1200px]:flex">
          <a href="/term-condition" className="text-[#7C4DFF] hover:underline">
            Terms &amp; Conditions
          </a>
          <span className="text-[#7C4DFF]" aria-hidden="true">|</span>
          <a href="/about-us" className="text-[#7C4DFF] hover:underline">
            About Us
          </a>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-sm min-[1200px]:hidden">
          <a href="/term-condition" className="text-[#7C4DFF] hover:underline">
            Terms &amp; Conditions
          </a>
          <span className="text-[#7C4DFF]" aria-hidden="true">|</span>
          <a href="/about-us" className="text-[#7C4DFF] hover:underline">
            About Us
          </a>
        </div>
      </div>
    </div>
  );
}
